import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { componentCriticalityKeys } from "@/application/query-keys/component-criticality.keys";
import {
  componentCriticalityRepository,
  type UpsertComponentCriticalityInput,
} from "@/infrastructure/repositories/component-criticality.supabase.repository";
import type { ComponentCriticalityRow } from "@/infrastructure/supabase/database.types";

/**
 * Returns the live criticality overrides as a `{ component_id: criticality }`
 * map, ready to pass to computeCandidateAggregate / criticalityOf. Components
 * without an override are absent from the map (the catalogue baseline applies).
 */
export function useComponentCriticality() {
  return useQuery({
    queryKey: componentCriticalityKeys.list(),
    queryFn: () => componentCriticalityRepository.list(),
    select: (rows): Record<string, number> => {
      const map: Record<string, number> = {};
      for (const r of rows) map[r.component_id] = r.criticality;
      return map;
    },
  });
}

export function useUpsertComponentCriticality() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertComponentCriticalityInput) =>
      componentCriticalityRepository.upsert(input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: componentCriticalityKeys.all });
      const listKey = componentCriticalityKeys.list();
      const previous = qc.getQueryData<ComponentCriticalityRow[]>(listKey);

      const now = new Date().toISOString();
      qc.setQueryData<ComponentCriticalityRow[]>(listKey, (current) => {
        const base = current ?? [];
        const filtered = base.filter((r) => r.component_id !== input.component_id);
        return [
          {
            component_id: input.component_id,
            criticality: input.criticality,
            created_at: now,
            updated_at: now,
          },
          ...filtered,
        ];
      });

      return { previous };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(componentCriticalityKeys.list(), ctx.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: componentCriticalityKeys.all });
    },
  });
}
